import { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import { faUsers } from '@fortawesome/pro-solid-svg-icons'
import { useMediaQuery } from '@react-hookz/web'
import type { CellContext, ColumnDef } from '@tanstack/react-table'
import { observer } from 'mobx-react'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { TableCard } from 'Components/TableCard/TableCard'

import { useStore } from 'Stores/index'

import {
  buttonize,
  formatDate,
  getFullContactName,
  isAnonymisedContact,
  maskAnonymisedContactValue,
} from 'Utilities'

import { PORTAL_BREAKPOINTS } from 'Constants/constants'

import type { ExitEmployee } from 'Types/dashboard/organisationDashboard.types'

import { composePath } from 'Portal/src/utilities/composePath/composePath.util'

export const EmployeeExitCard = observer(() => {
  const intl = useIntl()
  const { dashboardStore, portalSettingsStore } = useStore()
  const { exitEmployees } = dashboardStore
  const { i18n } = portalSettingsStore

  const { organisationId } = useParams<{
    organisationId?: string
  }>()
  const isMobile = useMediaQuery(`(max-width: ${PORTAL_BREAKPOINTS.LG})`)

  const navigate = useNavigate()

  const handleCellClick = useCallback(
    (
      event:
        | React.KeyboardEvent<HTMLDivElement>
        | React.MouseEvent<HTMLDivElement>,
      employeeId: number,
    ) => {
      event.preventDefault()

      const path = composePath({
        params: {
          employeeId,
          organisationId,
        },
        path: GLOBAL_ROUTE_PATHS.organisation.employee,
      })

      navigate(path)
    },
    [navigate, organisationId],
  )

  const renderEmployeeHeader = useCallback(
    () => <FormattedMessage id="Employee" />,
    [],
  )

  const renderExitDateHeader = useCallback(
    () => <FormattedMessage id="Exit_date" />,
    [],
  )

  const renderEmployeeCell = useCallback(
    ({ cell }: CellContext<ExitEmployee, unknown>) => {
      const employee = cell.row.original

      // an employee whose exit is long enough past has been anonymised, and their detail page
      // holds nothing to open any more
      const isAnonymised = isAnonymisedContact(employee)

      return (
        <div
          {...(isAnonymised ? {} : buttonize(handleCellClick, employee.id))}
          className="d-flex flex-column justify-content-center"
        >
          <div className="text-line-clamp fw-600">
            {getFullContactName(employee, { intl })}
          </div>

          <div className="d-flex table-text-subtitle text-muted">
            {maskAnonymisedContactValue(employee.email, employee, intl)}
          </div>
        </div>
      )
    },
    [handleCellClick, intl],
  )

  const renderExitDateCell = useCallback(
    ({ cell }: CellContext<ExitEmployee, unknown>) => {
      const employee = cell.row.original

      // every cell of the row opens the same detail page, so the date has to stop leading
      // there as well once the employee behind it is gone
      const isAnonymised = isAnonymisedContact(employee)

      return (
        <div
          {...(isAnonymised ? {} : buttonize(handleCellClick, employee.id))}
          className="d-flex align-items-center"
        >
          <span className="text-ellipsis">{formatDate(employee.exitDate)}</span>
        </div>
      )
    },
    [handleCellClick],
  )

  const columns = useMemo<ColumnDef<ExitEmployee>[]>(() => {
    const tableColumns = [
      {
        cell: renderEmployeeCell,
        header: renderEmployeeHeader,
        id: 'employee',
      },
    ]

    if (!isMobile) {
      tableColumns.splice(1, 0, {
        cell: renderExitDateCell,
        header: renderExitDateHeader,
        id: 'exitDate',
      })
    }

    return tableColumns
  }, [
    isMobile,
    renderEmployeeCell,
    renderEmployeeHeader,
    renderExitDateCell,
    renderExitDateHeader,
  ])

  const data = useMemo<ExitEmployee[]>(
    () => exitEmployees || [],
    [exitEmployees],
  )

  if (!exitEmployees) {
    return null
  }

  return (
    <TableCard
      className="mb-6"
      columns={columns}
      data={data}
      heading="EmployeeExitCard_heading"
      icon={faUsers}
      iconColor="orange"
      tableClassName="TableCard--unreturnedDevices"
      total={exitEmployees.length.toLocaleString(i18n.locale)}
    />
  )
})
